/**
 * Simple centralized in-memory store (module-level singleton) with subscription API.
 *
 * Purpose: minimal, reactive-ish surface for components that currently call
 * `addData(type, data)` in the screenshots. This keeps a plain object and
 * notifies subscribers when data changes.
 *
 * API:
 *  - addData(key, value)
 *  - getData(key)
 *  - getAllData()
 *  - removeData(key)
 *  - clearData()
 *  - subscribe(fn) -> unsubscribe function
 *  - unsubscribe(fn)
 *
 * Notes:
 *  - This is intentionally small and dependency-free. For React components prefer
 *    subscribing in a useEffect and updating local state. For a long-term solution
 *    the repo already uses Zustand; consider migrating to a zustand store later.
 */

export type CentralData = Record<string, any>;
export type Subscriber = (snapshot: Readonly<CentralData>) => void;

const centralizedData: CentralData = {};
const subscribers = new Set<Subscriber>();

function notify() {
  const snap = { ...centralizedData };
  subscribers.forEach((s) => {
    try {
      s(snap);
    } catch (err) {
      // avoid throwing in notifier; log so a single bad subscriber doesn't break others
      // eslint-disable-next-line no-console
      console.error('[centralized] subscriber error', err);
    }
  });
}

/** Set or replace data for the given key and notify subscribers. */
export function addData(key: string, value: any): void {
  centralizedData[key] = value;
  notify();
}

/** Get the value for a key (shallow reference). */
export function getData<T = any>(key: string): T | undefined {
  return centralizedData[key] as T | undefined;
}

/** Get a shallow copy of the entire store. */
export function getAllData(): CentralData {
  return { ...centralizedData };
}

/** Remove a key from the store. */
export function removeData(key: string): void {
  if (key in centralizedData) {
    delete centralizedData[key];
    notify();
  }
}

/** Clear all stored keys. */
export function clearData(): void {
  Object.keys(centralizedData).forEach((k) => delete (centralizedData as any)[k]);
  notify();
}

/** Subscribe to changes. Returns an unsubscribe function. The subscriber is called immediately with the current snapshot. */
export function subscribe(sub: Subscriber): () => void {
  subscribers.add(sub);
  try {
    sub({ ...centralizedData });
  } catch (err) {
    // ignore subscriber error on initial call
    // eslint-disable-next-line no-console
    console.error('[centralized] subscriber initial call error', err);
  }
  return () => subscribers.delete(sub);
}

/** Remove a previously added subscriber. */
export function unsubscribe(sub: Subscriber): void {
  subscribers.delete(sub);
}

/** Convenience default export with named methods. */
const centralized = {
  addData,
  getData,
  getAllData,
  removeData,
  clearData,
  subscribe,
  unsubscribe,
};

export default centralized;
